/**
@format
@flow
*/
import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from '@ziynx/components/icons';
import { SubHeading } from '@ziynx/components/typography';
import Colors from '@ziynx/theme/colors';

type Props = $ReadOnly<{|
    /**
     * Text for TextLinkCaret
     */
    children?: any,
    /**
     * Font and Icon sizes
     */
    small?: boolean,
    medium?: boolean,
    large?: boolean,
    /**
     * Hyperlink
     */
    href?: string,
    /**
     * Color for text and icon
     */
    color?: string,
    /**
     * optional onClick function
     */
    onClick?: (event?: SyntheticEvent<HTMLAnchorElement>) => any | Promise<any>,
    /**
     * optional target for link
     */
    target?: string,
    /**
     * Caret Position
     */
    iconPosition?: 'left' | 'right',
    /**
     * Disable link
     */
    disabled?: boolean,
|}>;

const StyledAnchor = styled.a`
    text-decoration: none;
    cursor: pointer;
    color: ${({ color }) => color};
`;

const Wrapper = styled.span`
    display: flex;
    align-items: center;
`;

const StyledSubHeading = styled(SubHeading)`
    ${({ iconPosition }) => (iconPosition === 'right' ? 'margin-right: 10px;' : 'margin-left: 10px;')}; 
`;

const findColor = (disabled, isActive, isHovering, color): string => {
  if (disabled) {
    return Colors.GrayLight;
  } if (!disabled && !isActive && isHovering) {
    return Colors.Gray;
  } if (!disabled && isActive) {
    return Colors.GrayVeryDark;
  }
  return color || Colors.GrayDark;
};

const TextLinkCaret = (props: Props): React$Node => {
  const {
    children,
    small,
    medium,
    large,
    href,
    color,
    onClick,
    target,
    iconPosition,
    disabled,
    ...rest
  } = props;

  const [isHovering, setHovering] = useState(false);
  const [isActive, setActive] = useState(false);

  const handleOnMouseEnter = (): void => {
    setHovering(true);
  };

  const handleOnMouseLeave = (): void => {
    setHovering(false);
  };

  const handleOnMouseDown = (): void => {
    setActive(true);
  };

  const handleOnMouseUp = (): void => {
    setActive(false);
  };

  return (
    <StyledAnchor
      href={href}
      onClick={onClick}
      target={target}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      onMouseDown={handleOnMouseDown}
      onMouseUp={handleOnMouseUp}
      isHovering={isHovering}
      isActive={isActive}
      {...rest}
    >
      <Wrapper>
        {iconPosition === 'left' && (
          <Icon
            name="chevron-left"
            color={findColor(disabled, isActive, isHovering, color)}
            isHovering={isHovering}
            isActive={isActive}
            mini
          />
        )}
        <StyledSubHeading
          iconPosition={iconPosition}
          small={small}
          medium={medium}
          large={!small && !medium}
          color={findColor(disabled, isActive, isHovering, color)}
          isHovering={isHovering}
          isActive={isActive}
          primitive="span"
        >
          {children}
        </StyledSubHeading>
        {iconPosition === 'right' && (
          <Icon
            name="chevron-right"
            color={findColor(disabled, isActive, isHovering, color)}
            isHovering={isHovering}
            isActive={isActive}
            mini
          />
        )}
      </Wrapper>
    </StyledAnchor>
  );
};

TextLinkCaret.defaultProps = {
  color: Colors.GrayDark,
  iconPosition: 'left',
};

export default TextLinkCaret;
